import React, { useEffect, useState } from 'react';
import { CircularProgress, Button, Box, CardContent, Typography, Card } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import Chart from 'react-apexcharts';
import { getSCAOverallScore as getSCAOverallScoreService } from 'services/modules/soc/agent';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const ConfigurationAssessment = ({ agentId, dataSend, agentName }) => {
    const id = agentId;

    // State Variables
    const [isDataEmpty, setIsDataEmpty] = useState(false);
    const [seriesValue, setSeriesValue] = useState(0);
    const [label, setLabel] = useState('');
    const [policyId, setPolicyId] = useState('');
    const [loading, setLoading] = useState(false);

    // React router
    const navigate = useNavigate();

    //Redux Selectors
    const selectedCompanyContainer = useSelector((state) => state.auth.companyData)

    // Method to fetch the overallSCAScore
    const getOverallScore = async () => {
        try {
            setLoading(true);
            let response = await getSCAOverallScoreService(id, selectedCompanyContainer?.soc_env);
            const fetchOverallScore = 100 - response?.data?.rawResponse?.hits?.hits?.[0]?._source?.data?.sca?.score;

            if (fetchOverallScore && fetchOverallScore >= 0) {
                setSeriesValue(fetchOverallScore);  // Set directly as a number
                setLabel(response?.data?.rawResponse?.hits?.hits?.[0]?._source?.data?.sca?.policy);
                setPolicyId(response?.data?.rawResponse?.hits?.hits?.[0]?._source?.data?.sca?.policy_id);
                setIsDataEmpty(false);
            } else {
                setIsDataEmpty(true);
                setSeriesValue("");
                setPolicyId("");
                setLabel("");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getOverallScore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id,selectedCompanyContainer?.soc_env ]);

    // Method handles the color based on the overall score
    const getColor = (value) => {
        if (value >= 0 && value <= 30) {
            return '#4E4EC2';
        } else if (value > 30 && value <= 70) {
            return '#ffa500eb';
        } else if (value > 70 && value <= 100) {
            return '#ff0000de';
        }
        return '#000000';
    };

    useEffect(() => {
        // Send policyId to parent component
        dataSend(policyId);
    }, [dataSend, policyId]);

    // State variable for radial chart properties
    const [state, setState] = useState({
        series: [seriesValue >= 0 && seriesValue],
        options: {
            chart: {
                height: 310,
                type: 'radialBar',
            },
            plotOptions: {
                radialBar: {
                    hollow: {
                        size: '65%',
                    },
                    track: {
                        background: '#F4F4F4',
                    },
                    dataLabels: {
                        show: true,
                        value: {
                            fontSize: '30px',
                            color: getColor(seriesValue),
                            fontWeight: 'bold',
                            align: 'center',
                            offsetY: -5,
                            formatter: function (val) {
                                return val === '' ? val + '' : val + '%'
                            }
                        },
                    },
                },
            },
            fill: {
                colors: [getColor(seriesValue)],
            },
            labels: [''],
        },
    });

    useEffect(() => {
        setState((prevState) => ({
            ...prevState,
            series: [seriesValue],
            options: {
                ...prevState.options,
                fill: { colors: [getColor(seriesValue)] },
                plotOptions: {
                    ...prevState.options.plotOptions,
                    radialBar: {
                        ...prevState.options.plotOptions.radialBar,
                        dataLabels: {
                            ...prevState.options.plotOptions.radialBar.dataLabels,
                            value: {
                                ...prevState.options.plotOptions.radialBar.dataLabels.value,
                                color: getColor(seriesValue),
                                fontSize: seriesValue >= 70 ? '24px' : '18px',
                            },
                        },
                    },
                },
            },
        }));
    }, [seriesValue]);

    // Handles to format label removing the version if exists in label
    const formatLabel = (text) =>{
        const match = text.match(/v?\d+\.\d+\.\d+/);
        const version = match ? match[0] : null;
        return version ? text.replace(version, "").trim() : text;
    }
  
    return (
        <div>
            <Card sx={{ margin: '10px', height: '450px' }}>
                <CardContent>
                    <>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faCog} className="soc-icon" />
                                <Typography variant="h6">Configuration Assessment</Typography>
                            </div>
                            {!isDataEmpty && (
                                <Button
                                    variant="contained"
                                    onClick={() =>
                                        navigate(`/soc/sca-report/?agentname=${agentName}&agentid=${agentId}&policyid=${policyId}`, { state: label })
                                    }

                                >
                                    View All
                                </Button>
                            )}
                        </Box>
                        <strong>{formatLabel(label)}</strong>
                        {loading ? (
                            <div className='d-flex justify-content-center align-items-center mt-5' >
                                <CircularProgress />
                            </div>
                        ) : (
                            <div className="soc-graph-container form-card">
                                <div className='full-width-field' id="chart-wrapper" style={{  height: '310px' }}>
                                    <Chart options={state.options} series={state.series} type="radialBar" height={310} />
                                    <div
                                        className='d-flex justify-content-center align-items-center'

                                    >
                                        <div className="d-flex align-items-center me-3">
                                            <div className='soc-legend'
                                                style={{
                                                    backgroundColor: 'red',
                                                }}
                                            ></div>
                                            Critical
                                        </div>
                                        <div className=" d-flex align-items-center me-3">
                                            <div className='soc-legend'
                                                style={{
                                                    backgroundColor: '#ffa500eb',
                                                   
                                                }}
                                            ></div>
                                            Medium
                                        </div>
                                        <div className=" d-flex align-items-center me-3">
                                            <div className='soc-legend ' style={{backgroundColor:"#4e4ec2"}}
                        
                                            ></div>
                                            Low
                                        </div>
                                    </div>
                                </div>



                                {isDataEmpty && (
                                    <div
                                        className="blur-effect"
                                    >
                                        No Details Found
                                    </div>
                                )}
                            </div>
                        )}

                    </>
                </CardContent>
            </Card>

        </div>
    );
};

export default ConfigurationAssessment;
